<template>
  <base-dialog @cancel="cancelDialog">
    <div slot="header">{{ $t('common.edit') }}</div>
    <div slot="body">
      <dialog-selected-tips :name="$t('subscription.rule')" :count="params.data.length" :action="$t('common.edit')" />
      <dialog-table :data="params.data" :columns="params.columns.slice(0, 5)" />
      <a-form-model ref="ruleForm" :model="formModel" :rules="rules" v-bind="formItemLayout">
        <a-form-model-item :label="$t('subscription.threshold')" :extra="thresholdExtra" prop="threshold">
          <a-input-number v-model="formModel.threshold" :min="1" :max="1000000000" /> {{ $t('subscription.threshold.unit_yuan') }}
        </a-form-model-item>
        <a-form-model-item :label="$t('subscription.triger_condition')">
          <a-row>
            <a-col :span="12" class="mr-2">
              <a-form-model-item :prop="formModel.term1" :extra="!formModel.showTerm2 && conditionExtra">
                <a-select v-model="formModel.term1" @change="term1ChangeHandle">
                  <a-select-option v-for="item in termOptions" :key="item.value" :value="item.value">
                    {{ item.label }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
            <a-col v-if="formModel.term1 === 'percentage'" :span="5">
              <a-input-number v-model="formModel[formModel.term1]" :min="1" /> %
            </a-col>
            <a-col v-if="formModel.term1 === 'absolute'" :span="5">
              <a-input-number v-model="formModel[formModel.term1]" :min="1" /> {{ $t('subscription.threshold.unit_yuan') }}
            </a-col>
            <a-col :span="2">
              <a-button
                v-show="!formModel.showTerm2"
                type="primary"
                shape="circle"
                icon="plus"
                size="small"
                @click="formModel.showTerm2 = true" />
            </a-col>
          </a-row>
          <template v-if="formModel.showTerm2">
            <a-row>
              <a-col :span="2">
                <a-form-model-item prop="condition">
                  <a-select v-model="formModel.condition">
                    <a-select-option v-for="item in conditionOptions" :key="item.value" :value="item.value">
                      {{ item.label }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
              </a-col>
            </a-row>
            <a-row>
              <a-col :span="12" class="mr-2">
                <a-form-model-item :prop="formModel.term2" :extra="conditionExtra">
                  <a-select v-model="formModel.term2" @change="term2ChangeHandle">
                    <a-select-option v-for="item in termOptions" :key="item.value" :value="item.value">
                      {{ item.label }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
              </a-col>
              <a-col v-if="formModel.term2 === 'percentage'" :span="5">
                <a-input-number v-model="formModel[formModel.term2]" :min="1" /> %
              </a-col>
              <a-col v-if="formModel.term2 === 'absolute'" :span="5">
                <a-input-number v-model="formModel[formModel.term2]" :min="1" /> {{ $t('subscription.threshold.unit_yuan') }}
              </a-col>
              <a-col :span="2">
                <a-button
                  type="primary"
                  shape="circle"
                  icon="minus"
                  size="small"
                  @click="formModel.showTerm2 = false" />
              </a-col>
            </a-row>
          </template>
        </a-form-model-item>
      </a-form-model>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading">{{ $t('dialog.ok') }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'

export default {
  name: 'AlertSettingUpdateDialog',
  mixins: [DialogMixin, WindowsMixin],
  data () {
    return {
      loading: false,
      formItemLayout: {
        wrapperCol: {
          span: 20,
        },
        labelCol: {
          span: 4,
        },
      },
      conditionOptions: [
        { label: this.$t('subscription.condition.and'), value: 'and' },
        { label: this.$t('subscription.condition.or'), value: 'or' },
      ],
      termOptions: [
        { label: this.$t('subscription.term.percentage'), value: 'percentage' },
        { label: this.$t('subscription.term.absolute'), value: 'absolute' },
      ],
      formModel: {
        term1: 'percentage',
        term2: 'absolute',
        threshold: 0,
        percentage: 0,
        absolute: 0,
        condition: 'or',
        showTerm2: false,
      },
      rules: {
        threshold: [
          { required: true, message: this.$t('common.field_required_placeholder', [this.$t('subscription.threshold')]), trigger: 'blur' },
        ],
      },
      thresholdExtra: this.$t('subscription.threshold.extra'),
    }
  },
  computed: {
    curData () {
      return this.params.data[0]
    },
    conditionExtra () {
      const { percentage, condition, absolute, showTerm2, term1 } = this.formModel
      const conditionVal = condition === 'or' ? this.$t('subscription.condition.or') : this.$t('subscription.condition.and')
      let conditionExtra = ''
      if (showTerm2) {
        conditionExtra = this.$t('subscription.percentage_absolute', [percentage + '%', conditionVal, absolute])
      } else if (term1 === 'percentage') {
        conditionExtra = this.$t('subscription.percentage', [percentage + '%'])
      } else if (term1 === 'absolute') {
        conditionExtra = this.$t('subscription.absolute', [absolute])
      }
      return conditionExtra
    },
  },
  watch: {
    curData: {
      handler (val) {
        this.init(val)
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    init (val) {
      const { threshold, percentage, absolute, condition } = val
      if (percentage && absolute) {
        this.formModel.showTerm2 = true
        this.formModel.term1 = 'percentage'
        this.formModel.term2 = 'absolute'
      } else if (percentage) {
        this.formModel.term1 = 'percentage'
        this.formModel.term2 = 'absolute'
      } else if (absolute) {
        this.formModel.term1 = 'absolute'
        this.formModel.term2 = 'percentage'
      }
      this.formModel.threshold = threshold
      this.formModel.percentage = percentage || 1
      this.formModel.absolute = absolute || 1
      this.formModel.condition = condition
    },
    term1ChangeHandle (v) {
      this.formModel.term2 = v === 'absolute' ? 'percentage' : 'absolute'
    },
    term2ChangeHandle (v) {
      this.formModel.term1 = v === 'absolute' ? 'percentage' : 'absolute'
    },
    doUpdate (data) {
      return new this.$Manager('bill_alert_settings').update({
        id: this.params.data[0].id,
        data,
      })
    },
    async handleConfirm () {
      this.loading = true
      try {
        this.$refs.ruleForm.validate(valid => {
          if (valid) {
            const { threshold, percentage, absolute, condition, showTerm2, term1, term2 } = this.formModel
            const params = {
              threshold,
              condition,
            }
            if (showTerm2) {
              params.percentage = percentage
              params.absolute = absolute
            } else {
              params[term1] = term1 === 'absolute' ? absolute : percentage
              params[term2] = 0
            }
            this.doUpdate(params)
          } else {
            console.log('error submit!!')
            return false
          }
        })
        this.loading = false
        this.$message.success(this.$t('common.success'))
        this.cancelDialog()
        this.params.refresh()
      } catch (error) {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped lang="less"></style>
